import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import { useBetween } from "use-between";
import BurgerStates from "./BurgerStates";
import BalanceStates from "../../games/BalanceStates";
import chat from "../../../assets/images/leftSidebar/menu-chat-icon.svg";
import games from "../../../assets/images/leftSidebar/menu-games-icon.svg";
import cons from "../../../assets/images/leftSidebar/menu-fair-icon.svg";
import token from "../../../assets/images/leftSidebar/menu-token-icon.svg";
import moderatorBonus from "../../../assets/images/leftSidebar/menu-faucet-icon.svg";
import stats from "../../../assets/images/leftSidebar/menu-stats-icon.svg";
import bonus from "../../../assets/images/leftSidebar/menu-bonus-icon.svg";
import exchange from "../../../assets/images/leftSidebar/menu-exchanger-icon.svg";
import {
    StyledAnimatedProfit,
    StyledBalance, StyledBonus,
    StyledBurger,
    StyledChat,
    StyledEnter, StyledExchange,
    StyledFair,
    StyledGames, StyledLegendBonus,
    StyledMobileNavWrapper, StyledModeratorBonus,
    StyledMoney,
    StyledNavAvatar,
    StyledStats,
    StyledToken, StyledVipBonus
} from "./styledMobileNavigation";
import { AppContext } from "../../../App";
import {LeftSidebarMenu, privileges, responseStatus, roles, showModal} from "../../../utils/consts";
import LoginContainer from "../../login/LoginContainer";
import RegistrationContainer from "../../registration/RegistrationContainer";
import { useAnimationSyncData } from '../../games/AnimationSyncDataStates';
import Dialog from "rc-dialog";
import { useTranslation } from "react-i18next";
import { StyledFlexAlign } from "../inputGroup/styledInputGroup";
import { VisibleLeftSidebarStates } from "../leftSidebar/VisibleLeftSidebarStates";
import http from "../../../http";
import userAuthenticationConfig from "../../../utils/userAuthenticationConfig";
import defaultImg from "../../../assets/images/no-avatar.svg";
import { closableNotification } from "../notification/ClosableNotification";
import { StyledPaymentMethodsIcons } from "../../styles/StyledPaymentMethodsIcons";
import {useLayout} from "../../../utils/useLayout";
import ForgotPassword from "../../forgotPassword/ForgotPassword";

const MobileNavigation = ({paymentMethod}) => {
    const [visible, setVisible] = useState("");

    const { authenticated, user, userPrivileges } = useContext(AppContext);

    const {
        isOpenBurger,
        setIsOpenBurger,
        isOpenCurrency,
        setIsOpenCurrency,
        isOpenChat,
        setIsOpenChat,
        setIsOpenProfile,
        setIsOpenBalance,
        isBalanceOpen,
        balanceRef,
    } = useBetween(BurgerStates);
    const {
        setVisible: setLeftSidebarVisible,
        setTokenBonusVisible,
        setGameBonusVisible,
        setFaucetsVisible,
        menu,
        setMenu,
        setExchangeVisible
    } = useBetween(VisibleLeftSidebarStates);
    const {isMobile} = useLayout();

    const showMenu = useCallback((type) => {
        setLeftSidebarVisible(true);
        setMenu(type);

        if (menu === type) {
            setLeftSidebarVisible(false);
            setMenu("");
        }
    }, [menu, setLeftSidebarVisible, setMenu]);

    const { balance, activeCurrency, profit } = useBetween(BalanceStates);

    const { t } = useTranslation('header');
    const { t: tO } = useTranslation('siteOptions');

    const setOpenBurger = () => setIsOpenBurger(!isOpenBurger);

    const setOpenCurrency = () => setIsOpenCurrency(!isOpenCurrency);

    const setOpenChat = () => setIsOpenChat(!isOpenChat);

    const dataBalanceInfo = useMemo(() => ({ balance, profit }), [balance, profit]);
    const balanceInfo = useAnimationSyncData(dataBalanceInfo);

    const [avatar, setAvatar] = useState({ loaded: false, base64: null });
    const [userLevel, setUserLevel] = useState(0);
    const [changesQueue, setChangesQueue] = useState([]);

    const formatBalance = (num, precision = 8) => {
        const str = num.toString();
        const parts = str.split('.');
        if (parts?.length === 1) {
            return `${str}.${'0'.repeat(precision)}`;
        }
        if (parts[1]?.length < precision) {
            return `${parts[0]}.${parts[1].padEnd(precision, '0')}`;
        }
        return `${parts[0]}.${parts[1].slice(0, precision)}`;
    };

    const displayBalance = useMemo(() => {
        return formatBalance(balanceInfo.balance);
    }, [balanceInfo.balance]);

    useEffect(() => {
        if (balanceRef.current && balanceInfo.balance) {
            balanceRef.current.textContent = formatBalance(balanceInfo.balance);
        }
    }, [balanceInfo.balance]);

    useEffect(() => {
        if (balanceInfo.profit.win || balanceInfo.profit.lose) {
            const id = Math.random().toString(16).slice(2);
            setChangesQueue(old => [
                ...old,
                {
                    id: id,
                    profit: balanceInfo.profit,
                }
            ]);
            setTimeout(() => {
                setChangesQueue(old => old?.filter(item => item.id !== id))
            }, 1500)
        }
    }, [balanceInfo.profit]);

    useEffect(() => {
        const abortController = new AbortController();
        if (authenticated) {
            http.get("/api/media-objects", { ...userAuthenticationConfig(), signal: abortController.signal })
                .then(response => {
                    if (response.status === responseStatus.HTTP_OK && response.data.base64 !== "") {
                        setAvatar({ loaded: true, base64: response.data.base64 ?? defaultImg });
                    } else {
                        setAvatar({ loaded: true, base64: defaultImg });
                    }
                })
                .catch(error => {
                    setAvatar({ loaded: true, base64: defaultImg });
                    if (error.response.status === responseStatus.HTTP_BAD_REQUEST) {
                        closableNotification(error.response.data.error, "error");
                    }
                });
        }
        return () => {
            abortController.abort();
        }
    }, [authenticated]);

    const getUserLevel = async () => {
        http.get(`${process.env.REACT_APP_LEVEL_LINK}/api/profile_level/${user?.nickname}`, { ...userAuthenticationConfig()})
            .then(response => {
                if (response.status === responseStatus.HTTP_OK && response.data.base64 !== "") {
                    setUserLevel(response?.data?.level);
                }
            })
            .catch(error => {

            });
    }

    useEffect(() => {
        if (!authenticated) return;
        getUserLevel();
    }, [authenticated])


    useEffect(() => {
        if ((menu === "Chat" || isOpenChat) && isMobile) {
            document.documentElement.style.overflowY = "hidden";
            return document.body.style.overflowY = "hidden";
        }
        if ((isOpenBurger || isOpenCurrency || isBalanceOpen) && isMobile) {
            document.documentElement.style.overflowY = "hidden";
            return document.body.style.overflowY = "hidden";
        }
        if (((menu !== "Chat" || !isOpenChat) && !isOpenBurger && !isOpenCurrency && !isBalanceOpen) && isMobile) {
            document.documentElement.style.overflowY = "";
            document.body.style.overflowY = "";
        }
    }, [menu, isMobile, isOpenChat, isOpenCurrency, isOpenBurger, isBalanceOpen]);

    const profits = useMemo(() => {
        return changesQueue?.map((item, key) => (
            <StyledAnimatedProfit
                key={item.id}
                animationDuration={200}
                id={`notification_${item.id}`}
                win={item.profit.win}
            >
                {item.profit.win || item.profit.lose}
            </StyledAnimatedProfit>
        ));
    }, [changesQueue]);

    useEffect(() => {
        if (visible) {
            window.history.pushState({modal: true}, '');

            const handlePopState = () => {
                setVisible("");
            };

            window.addEventListener('popstate', handlePopState);
            return () => window.removeEventListener('popstate', handlePopState);
        }
    }, [visible, setVisible]);

    return (
        <StyledMobileNavWrapper>
            <Dialog
                visible={visible}
                wrapClassName="default-modal-wrapper"
                onClose={() => setVisible("")}
                animation="zoom"
                maskAnimation="fade"
                title={visible === showModal.LOGIN ? t("login") : visible === showModal.FORGOT_PASSWORD ? tO("forgotYourPasswordTitle") : t("createAccount")}
                forceRender={false}
                className="default-modal"
            >
                <div className="default-modal__content">
                    {visible === showModal.LOGIN ? <LoginContainer visible={visible} setVisible={setVisible} /> :
                        visible === showModal.FORGOT_PASSWORD ? <ForgotPassword setVisible={setVisible} /> :
                            <RegistrationContainer setVisible={setVisible} />
                    }
                </div>

            </Dialog>

            <StyledFlexAlign>
                <StyledBurger onClick={setOpenBurger} isOpen={isOpenBurger}>
                    <span></span>
                    <span></span>
                    <span></span>
                </StyledBurger>
                <StyledGames onClick={() => showMenu(LeftSidebarMenu.GAMES)}>
                    <img src={games} alt="games"/>
                </StyledGames>
                <StyledChat onClick={setOpenChat}>
                    <img src={chat} alt="chat"/>
                </StyledChat>
                <StyledFair onClick={() => showMenu(LeftSidebarMenu.CONTROL)}>
                    <img src={cons} alt="fair"/>
                </StyledFair>
                {
                    authenticated &&
                    <>
                        <StyledStats onClick={() => showMenu(LeftSidebarMenu.STATISTICS)}>
                            <img src={stats} alt="stats"/>
                        </StyledStats>
                        <StyledBonus onClick={() => {
                            setGameBonusVisible(true);
                        }}>
                            <img src={bonus} alt="bonus"/>
                        </StyledBonus>
                        <StyledToken onClick={() => {
                            setTokenBonusVisible(true);
                        }}>
                            <img src={token} alt="token"/>
                        </StyledToken>
                        <StyledExchange onClick={() => setExchangeVisible(true)}>
                            <img src={exchange} alt="exchange"/>
                        </StyledExchange>
                        {((user?.roles[0] === roles.MODERATOR || user?.roles[0] === roles.ADMIN) || userPrivileges?.some(prev => prev?.type === privileges.LEGEND) || userPrivileges?.some(prev => prev?.type === privileges.VIP)) &&
                            <StyledModeratorBonus onClick={() => {
                                setFaucetsVisible(true);
                            }}>
                                <img src={moderatorBonus} alt="token"/>
                            </StyledModeratorBonus>}
                    </>
                }
            </StyledFlexAlign>
            <StyledFlexAlign>
                {
                    authenticated ?
                        <>
                            <StyledMoney onClick={() => {
                                if (!paymentMethod) return;
                                setOpenCurrency()
                            }} isDisabled={!paymentMethod}>
                                <StyledPaymentMethodsIcons className={`payment-method-${activeCurrency}`}/>
                            </StyledMoney>
                            <StyledBalance isDisabled={!paymentMethod}  onClick={() => {
                                if (!paymentMethod) return;
                                setIsOpenBalance()
                            }}>
                                <span ref={balanceRef}>
                                    {displayBalance}
                                </span>
                                {profits}
                            </StyledBalance>
                            {/*<Balance ref={balanceRef} balance={balanceInfo.balance} bet={balanceInfo.profit}/>*/}
                            <StyledNavAvatar loading={avatar.loaded ? undefined : 1} onClick={setIsOpenProfile}>
                                {avatar.loaded ? <img src={avatar.base64} alt="user-image"/> : <div className={'user-image_shimmer'}/>}
                                <div className={`rating ${userLevel && userLevel > 9 ? "level" + userLevel : ""}`} title={t('accountRating')}>
                                    {userLevel}
                                </div>
                                {(user?.roles[0] === roles.MODERATOR || user?.roles[0] === roles.ADMIN) && <div
                                    className={`user-group ${user?.roles[0] === roles.MODERATOR ? "mod" : user?.roles[0] === roles.ADMIN ? "admin" : ""}`}>
                                    {user?.roles[0] === roles.MODERATOR ? "MOD" : user?.roles[0] === roles.ADMIN ? "ADMIN" : ""}
                                </div>}
                                {user?.roles[0] && user?.roles[0] === roles.CLIENT && userPrivileges && userPrivileges?.length > 0 && (() => {
                                    const hasLegend = userPrivileges.some(prev => prev.type === privileges.LEGEND);
                                    const hasVIP = userPrivileges.some(prev => prev.type === privileges.VIP);
                                    const displayStatus = hasLegend ? "LEGEND" : (hasVIP ? "VIP" : "");
                                    const className = hasLegend ? "leg" : (hasVIP ? "vip" : "");
                                    return <div className={`user-group ${className}`}>{displayStatus}</div>;
                                })()}
                            </StyledNavAvatar>
                        </> :
                        <>
                            <StyledEnter onClick={() => setVisible(showModal.LOGIN)}>
                                {t('logIn')}
                            </StyledEnter>
                            <StyledEnter onClick={() => setVisible(showModal.REGISTRATION)}>
                                {t('signUp')}
                            </StyledEnter>
                        </>
                }
            </StyledFlexAlign>
        </StyledMobileNavWrapper>
    );
};

export default MobileNavigation;
